import CheckboxField from "@/components/CheckboxField";
import Form from "@/components/Form";
import TextField from "@/components/TextField";
import { useAuthContext } from "@/contexts/AuthContext";
import { SessionContext } from "@/contexts/SessionContext";
import useGTM from "@/hooks/useGTM";
import { useStores } from "@/hooks/useStores";
import { pushEvent } from "@/lib/gtm";
import { Button } from "@/src/components/ui/Button";
import { useCroWeb178Experiment } from "@/src/experiments/cro-web-178/useCroWeb178Experiment";
import { userAccount } from "@/src/features/localization/en";
import clsx from "clsx";
import { Formik } from "formik";
import { AnimatePresence, motion } from "framer-motion";
import { passwordHint, passwordRegex } from "lib/constants";
import { observer } from "mobx-react";
import Link from "next/link";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import { useContext, useEffect, useState } from "react";
import * as Yup from "yup";
import SocialAuthButtons from "../SocialAuthButtons";
import ContinueWithEmailButton from "./ContinueWithEmailButton";

const SignUpForm = observer(({ onStatusChange = (value) => undefined }) => {
  const gtm = useGTM();
  const { authStatus, setAuthStatus, currentEmail, setCurrentEmail } =
    useAuthContext();
  const { shouldShowEmailButton } = useCroWeb178Experiment();

  // Local state for email form visibility
  const [showEmailForm, setShowEmailForm] = useState(false);

  const { setUserId } = useContext(SessionContext);

  const router = useRouter();

  // Reset auth status on mount
  useEffect(() => {
    setAuthStatus("idle");
  }, []);

  // Initialize email form visibility based on experiment
  useEffect(() => {
    // For control variant or when not in experiment, show form immediately
    // For continue-with-email variant, hide form initially
    setShowEmailForm(!shouldShowEmailButton);
  }, [shouldShowEmailButton]);

  // set couponCode to use in stripe at checkout
  useEffect(() => {
    if (router.isReady) {
      if (router.query["coupon_code"]) {
        sessionStorage.setItem(
          "couponCode",
          router.query["coupon_code"] as string
        );
      } else if (process.env.NEXT_PUBLIC_AUTO_APPLIED_COUPON) {
        sessionStorage.setItem(
          "couponCode",
          process.env.NEXT_PUBLIC_AUTO_APPLIED_COUPON
        );
      } else if (
        !process.env.NEXT_PUBLIC_AUTO_APPLIED_COUPON &&
        sessionStorage.getItem("couponCode")
      ) {
        sessionStorage.removeItem("couponCode");
      }
    }
  }, [router.isReady]);

  const { profileStore, authStore } = useStores();
  const { setIsProfileWizardActive } = profileStore;

  const handleSubmit = (values, actions) => {
    posthog.capture("onboarding_signup_button_click");

    const { setSubmitting, setErrors } = actions;

    const submitAction = async () => {
      // Convert inverse checkbox value to boolean for marketingConsent
      const processedValues = {
        ...values,
        marketingConsent: values.marketingConsent !== 0,
      };

      // Sign the user up
      const signUpResponse = await authStore.signUp(processedValues);

      // If there are errors, set them on the form
      if (!signUpResponse.success) {
        // if error is for email already taken, set authStatus to "exists"
        if (
          signUpResponse.error.email &&
          signUpResponse.error.errorType === "UserAlreadyRegisteredError"
        ) {
          setAuthStatus("exists");
        } else {
          setErrors(signUpResponse.error);
          setAuthStatus("error");
        }

        setSubmitting(false);

        return false;
      }

      setAuthStatus("success");

      authStore.setIsAuthActive(false);
      setIsProfileWizardActive(true);
      setSubmitting(false);
      setUserId(authStore.currentUser.id);

      const onboardingAnswers = sessionStorage.getItem("onboardingAnswers");

      if (onboardingAnswers && authStore.currentUser) {
        const parsed = JSON.parse(onboardingAnswers);

        authStore.currentUser.onboardingData = JSON.stringify(parsed);

        try {
          await authStore.currentUser.save();
          sessionStorage.removeItem("onboardingAnswers");
        } catch (err) {
          console.error("[SignUp] Failed to save onboarding data:", err);
        }

        sendSuccessEvents();
      }
    };

    submitAction();
  };

  useEffect(() => {
    if (authStatus) onStatusChange(authStatus);
  }, [setAuthStatus, onStatusChange, authStatus]);

  const sendSuccessEvents = () => {
    if (authStore.currentUser && authStore.currentUser.plan === 3) {
      gtm.pushEvent("free_account_created");

      pushEvent("account_signup", {
        user_id: authStore.currentUser.id,
      });

      posthog.capture("onboarding_signup_success");
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: currentEmail || "",
          password: "",
          marketingConsent: 1,
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={true}
      >
        {({
          isSubmitting,
          handleReset,
          handleSubmit,
          values,
          errors,
          handleChange,
          setErrors,
        }) => {
          const handleEmailChange = (ev) => {
            handleChange(ev);
            setCurrentEmail(ev.target.value);
            setErrors({});
            if (authStatus !== "idle") setAuthStatus("idle");
          };

          const showEmailExistsMessage = authStatus === "exists";

          return (
            <Form handleReset={handleReset} handleSubmit={handleSubmit}>
              <div className="space-y-6">
                <div className="space-y-2">
                  <SocialAuthButtons
                    theme="light"
                    onSuccess={sendSuccessEvents}
                  />

                  {shouldShowEmailButton && !showEmailForm && (
                    <ContinueWithEmailButton
                      onClick={() => {
                        setShowEmailForm(true);
                        posthog.capture("continue_with_email_button_click");
                      }}
                      theme="light"
                    />
                  )}
                </div>
                <AnimatePresence>
                  {showEmailForm && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{
                        duration: 0.3,
                        ease: "easeOut",
                      }}
                      className="space-y-6"
                    >
                      <TextField
                        name="email"
                        label="Email"
                        placeholder="Enter your email"
                        type="email"
                        theme="light"
                        value={values.email}
                        onChange={handleEmailChange}
                        attributes={{
                          "data-testid": "signup-email-input",
                        }}
                      />

                      <FieldsGroup
                        authStatus={authStatus}
                        isSubmitting={isSubmitting}
                        formikValues={values}
                        formikErrors={errors}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {showEmailExistsMessage && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="font-body text-sm"
                    >
                      It looks like you might already have an account.{" "}
                      <Link
                        href="/signin"
                        className="underline underline-offset-2 transition hover:opacity-75"
                        data-posthog="signup-form-login-link"
                      >
                        Log In instead.
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
});

const FieldsGroup = ({
  authStatus,
  isSubmitting,
  formikValues,
  formikErrors,
}) => {
  const isLoading = isSubmitting || authStatus === "success";
  const buttonText = "Continue";

  return (
    <div className={clsx("space-y-6", {})}>
      <div className={clsx("flex gap-4")}>
        <div className={clsx("w-1/2")}>
          <TextField
            name="firstName"
            label="First name"
            placeholder="Enter your first name"
            type="text"
            theme="light"
            attributes={{
              "data-testid": "signup-firstname-input",
            }}
          />
        </div>
        <div className={clsx("w-1/2")}>
          <TextField
            name="lastName"
            label="Last name"
            placeholder="Enter your last name"
            type="text"
            theme="light"
            attributes={{
              "data-testid": "signup-lastname-input",
            }}
          />
        </div>
      </div>

      <div>
        <TextField
          name="password"
          label="Password"
          placeholder="Enter a password"
          type="password"
          theme="light"
          attributes={{
            "data-testid": "signup-password-input",
          }}
        />
      </div>

      <CheckboxField
        name="marketingConsent"
        label={userAccount.marketingConsentLabel}
        inverse
      />

      <div className={clsx("")}>
        <Button
          type="submit"
          disabled={isLoading}
          className={clsx("w-full", {
            "animate-tailwindBounce": !isLoading,
          })}
          data-posthog="signup-form-submit-button"
          data-testid="signup-submit-button"
        >
          {isLoading ? "Please wait..." : buttonText}
        </Button>
      </div>

      <p className="text-center font-body text-sm text-zinc-600">
        By signing up you agree to Mob&apos;s{" "}
        <Link
          href="/mob-plus-terms-and-conditions"
          className="underline underline-offset-2 hover:opacity-75"
          target="_blank"
        >
          Terms and Conditions
        </Link>
      </p>

      {process.env.NODE_ENV === "development" && (
        <>
          {/* Useful for debugging formik values and errors: remove hidden classname to show */}
          <pre className="hidden pt-12 text-xs">
            <strong>Formik Values:</strong>
            {JSON.stringify(formikValues, null, 2)}
            <br />
            <strong>Formik Errors:</strong>
            {JSON.stringify(formikErrors, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("Your first name is required"),
  lastName: Yup.string().required("Your last name is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Your email address is required"),
  password: Yup.string()
    .required("Please enter a password")
    .matches(passwordRegex, passwordHint),
});

export default SignUpForm;
